import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Users,
  GraduationCap,
  Bus,
  Briefcase,
  HeartHandshake,
  CheckCircle2,
  Calendar,
  Phone,
  Mail,
  Send,
  FileText,
  Clock,
  Sparkles,
  MapPin,
  Coffee,
  Utensils,
  Projector,
  Mic2,
  Car,
  ChevronDown,
  ArrowRight,
  ShieldCheck,
  Award
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { trackWhatsAppClick, trackEvent, trackPageView } from '../../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CorporateGroup = () => {
  const [content, setContent] = useState({});
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    role_title: '',
    phone: '',
    email: '',
    group_type: 'Gathering Perusahaan',
    participants_count: '',
    check_in: '',
    check_out: '',
    requirements: [
      'Ruang Meeting / Ballroom',
      'Kamar Menginap Rombongan',
      'Coffee Break & Prasmanan'
    ],
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    document.title = "Paket Corporate Gathering & Rombongan di Batu Malang | Spencer Green Hotel";
    trackPageView('/corporate-gathering-batu');

    const fetchPageContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/content/corporate_group`);
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(item => {
            map[item.section] = item.content;
          });
          setContent(map);
        }
      } catch (err) {
        console.error('Failed to fetch corporate_group content:', err);
      }
    };

    fetchPageContent();
  }, []);

  const handleRequirementToggle = (req) => {
    setFormData(prev => {
      const exists = prev.requirements.includes(req);
      if (exists) {
        return { ...prev, requirements: prev.requirements.filter(r => r !== req) };
      } else {
        return { ...prev, requirements: [...prev.requirements, req] };
      }
    });
  };

  const generateWhatsAppMessage = (data = formData) => {
    return `*PERMINTAAN PENAWARAN (RFP) - SPENCER GREEN HOTEL BATU*
----------------------------------------
*Instansi / Perusahaan:* ${data.company_name || '-'}
*Nama PIC:* ${data.contact_name || '-'} ${data.role_title ? `(${data.role_title})` : ''}
*No. WhatsApp / HP:* ${data.phone || '-'}
*Email:* ${data.email || '-'}
*Kategori Acara:* ${data.group_type}
*Jumlah Peserta:* ${data.participants_count ? `${data.participants_count} Pax` : '-'}
*Rencana Tanggal:* ${data.check_in || '-'} s/d ${data.check_out || '-'}

*Kebutuhan Fasilitas:*
${data.requirements && data.requirements.length > 0 ? data.requirements.map(r => `• ${r}`).join('\n') : '• Paket Standar'}

*Catatan / Kebutuhan Khusus:*
${data.notes || '-'}
----------------------------------------
Mohon dapat dikirimkan surat penawaran harga & proposal resmi. Terima kasih.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.contact_name || !formData.phone) {
      toast.error('Mohon lengkapi Nama Instansi/Perusahaan, Nama PIC, dan Nomor WhatsApp');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/inquiries`, formData);
      setIsSubmitted(true);
      toast.success('Formulir penawaran berhasil dikirim! Menghubungkan ke WhatsApp Sales...');
      trackEvent('Inquiry', 'submit_rfp', formData.group_type);

      setTimeout(() => {
        const message = generateWhatsAppMessage(formData);
        window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(message)}`, '_blank');
      }, 800);
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      toast.info('Menghubungkan langsung ke WhatsApp Corporate Sales...');
      const message = generateWhatsAppMessage(formData);
      window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(message)}`, '_blank');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDirectWhatsApp = (topic = 'Corporate Sales Inquiry') => {
    trackWhatsAppClick(`Corporate Landing - ${topic}`);
    const message = `Halo Sales & Marketing Spencer Green Hotel Batu, kami berencana mengadakan acara rombongan/gathering. Mohon info pricelist dan ketersediaan tanggal.`;
    window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(message)}`, '_blank');
  };

  const heroContent = content.hero || {};
  const salesBannerContent = content.sales_banner || {};

  const targetAudiences = [
    {
      title: content.segment_1?.title || "Instansi Pemerintah & BUMN",
      icon: <Building2 className="w-8 h-8 text-emerald-600" />,
      tag: content.segment_1?.tag || "Raker, Bimtek & Konsinyering",
      desc: content.segment_1?.desc || "Didukung fasilitas meeting berstandar kedinasan, kelengkapan administrasi SPJ & invoice resmi, ruang sidang representatif, dan paket fullboard meeting fleksibel.",
      bullets: ["Kelengkapan SPJ & Dokumen Resmi", "Ruang Sidang & Breakout Room", "Paket Fullboard Meeting Nyaman"]
    },
    {
      title: content.segment_2?.title || "Gathering Perusahaan & Outing",
      icon: <Briefcase className="w-8 h-8 text-emerald-600" />,
      tag: content.segment_2?.tag || "Employee Gathering & Outbound",
      desc: content.segment_2?.desc || "Perkuat soliditas tim di udara sejuk Kota Batu. Tersedia halaman outdoor hijau untuk ice breaking / outbound, panggung musik, gala dinner prasmanan, dan doorprize venue.",
      bullets: ["Area Lapangan Hijau untuk Outbound", "Sound System Panggung & Lighting", "Gala Dinner Prasmanan Nusantara"]
    },
    {
      title: content.segment_3?.title || "Sekolah & Universitas",
      icon: <GraduationCap className="w-8 h-8 text-emerald-600" />,
      tag: content.segment_3?.tag || "Study Tour, LDKS & Perpisahan",
      desc: content.segment_3?.desc || "Akomodasi aman dan terpercaya untuk rombongan pelajar & mahasiswa. Lokasi strategis hanya 5 menit ke Selecta, menu prasmanan higienis, dan kapasitas kamar fleksibel.",
      bullets: ["Tarif Bersahabat untuk Pelajar", "Akses 5 Menit ke Wisata Selecta", "Area Aman & Pengawasan 24 Jam"]
    },
    {
      title: content.segment_4?.title || "Rombongan Wisata (Tour & Travel)",
      icon: <Bus className="w-8 h-8 text-emerald-600" />,
      tag: content.segment_4?.tag || "Family Tour & Biro Perjalanan",
      desc: content.segment_4?.desc || "Parkir bus pariwisata yang luas di dalam hotel, proses check-in rombongan cepat dan terkoordinasi, serta transit makan prasmanan yang higienis dan lezat.",
      bullets: ["Parkir Bus Luas & Aman di Dalam Hotel", "Proses Check-in Rombongan Cepat", "Kamar Luas Berkapasitas Rombongan"]
    },
    {
      title: content.segment_5?.title || "Komunitas & Organisasi",
      icon: <HeartHandshake className="w-8 h-8 text-emerald-600" />,
      tag: content.segment_5?.tag || "Kopdar, Klub Motor/Mobil & Reuni",
      desc: content.segment_5?.desc || "Tempat berkumpul yang asyik dan prestisius. Bersantai di rooftop cafe Rintik Rindu, berfoto dengan latar pegunungan megah, dan fasilitas barbecue santai.",
      bullets: ["Venue Rooftop Cafe Ber-View Gunung", "Ramah Parkir Kendaraan Komunitas", "Paket BBQ & Live Acoustic Ready"]
    }
  ];

  const facilitiesList = [
    {
      title: content.facility_1?.title || "Meeting Rooms & Ballroom",
      desc: content.facility_1?.desc || "Pilihan ruang fleksibel: Boardroom, Class Room, hingga Conference Hall berkapasitas hingga 200 peserta lengkap dengan AC dan pencahayaan optimal.",
      icon: <Projector className="w-6 h-6" />
    },
    {
      title: content.facility_2?.title || "Audio Visual & Sound System",
      desc: content.facility_2?.desc || "Screen projector HD, wireless microphones, sound system panggung berkualitas, podium pembicara, flipchart, dan sambungan internet berkecepatan tinggi.",
      icon: <Mic2 className="w-6 h-6" />
    },
    {
      title: content.facility_3?.title || "Parkir Bus Pariwisata",
      desc: content.facility_3?.desc || "Area parkir luas di dalam kompleks hotel untuk mobil pribadi, minibus Elf, hingga bus besar pariwisata dengan penjagaan keamanan 24 jam.",
      icon: <Car className="w-6 h-6" />
    },
    {
      title: content.facility_4?.title || "Catering Prasmanan Berskala Besar",
      desc: content.facility_4?.desc || "Kenari Restaurant siap menyajikan sarapan, makan siang, gala dinner, dan coffee break tepat waktu dengan standar higienis dan rasa masakan yang lezat.",
      icon: <Utensils className="w-6 h-6" />
    }
  ];

  const packages = [
    {
      name: content.package_1?.name || "Half Day Meeting",
      duration: content.package_1?.duration || "4 Jam Pemakaian",
      price: content.package_1?.price || "Mulai Rp 175.000 /pax",
      includes: [
        "Ruang Meeting ber-AC Dingin",
        "1x Coffee Break Gurih & Manis",
        "Screen Projector & Wireless Mic",
        "Meeting Stationery (Note & Pen)",
        "Standard Sound System & High Speed WiFi",
        "Air Mineral & Permen"
      ]
    },
    {
      name: content.package_2?.name || "Full Day Meeting",
      duration: content.package_2?.duration || "8 Jam Pemakaian",
      price: content.package_2?.price || "Mulai Rp 275.000 /pax",
      badge: "Paling Populer",
      includes: [
        "Ruang Meeting Full Setup (U-Shape/Classroom)",
        "2x Coffee Break (Pagi & Sore)",
        "1x Makan Siang Prasmanan Kenari Restaurant",
        "Screen Projector, Mic & Podium",
        "Meeting Stationery & Flipchart",
        "Sound System & High Speed WiFi"
      ]
    },
    {
      name: content.package_3?.name || "Fullboard Residential",
      duration: content.package_3?.duration || "Menginap 2 Hari 1 Malam",
      price: content.package_3?.price || "Mulai Rp 650.000 /pax",
      includes: [
        "Akomodasi Kamar Menginap Nyaman (Twin/Triple)",
        "Ruang Meeting & Ballroom Pemakaian Penuh",
        "3x Makan Prasmanan (Pagi, Siang, Malam)",
        "2x Coffee Break Premium",
        "Fasilitas Kolam Renang & Rooftop Cafe",
        "Kelengkapan Audio Visual & Fasilitas Lengkap"
      ]
    },
    {
      name: content.package_4?.name || "Outbound & Fun Gathering",
      duration: content.package_4?.duration || "1 Hari / Menyesuaikan",
      price: content.package_4?.price || "Custom Sesuai Kebutuhan",
      includes: [
        "Area Lapangan Hijau untuk Fun Games",
        "Fasilitator & Master Game Outbound Berpengalaman",
        "Makan Siang Prasmanan / Gala Dinner",
        "Coffee Break & Refreshment Kelapa Muda/Teh",
        "Sound System Portable & Panggung Acara",
        "Dokumentasi & Spanduk Acara (Opsional)"
      ]
    }
  ];

  const availableRequirements = [
    'Ruang Meeting / Ballroom',
    'Kamar Menginap Rombongan',
    'Coffee Break & Prasmanan',
    'Area Outbound & Fun Games',
    'Gala Dinner & Panggung Musik',
    'Area Parkir Khusus Bus Pariwisata',
    'Dokumen SPJ / Faktur Pajak Resmi',
    'Paket Wisata ke Selecta'
  ];

  const faqs = [
    {
      q: "Berapa kapasitas maksimal ruang pertemuan di Spencer Green Hotel?",
      a: "Kami memiliki beberapa opsi ruangan, mulai dari Boardroom (15-25 pax), Training Room (30-50 pax), hingga Conference Ballroom yang dapat menampung hingga 150-200 peserta dengan setup theater atau 120 pax dengan setup banquet/meja bundar."
    },
    {
      q: "Apakah area parkir muat untuk bus pariwisata besar (Big Bus 50-60 seat)?",
      a: "Ya! Spencer Green Hotel memiliki area parkir luas di halaman hotel yang dapat menampung bus pariwisata berukuran besar secara aman dengan akses manuver yang leluasa dan pengamanan 24 jam."
    },
    {
      q: "Apakah melayani instansi pemerintah dengan sistem SPJ dan pembayaran invoice?",
      a: "Tentu. Kami sangat terbiasa menangani kegiatan kementerian, dinas pemprov/pemkab/kota, dan BUMN. Kami menyediakan invoice resmi, kwitansi bermaterai, kelengkapan surat penawaran, dan dokumen perpajakan lengkap sesuai regulasi."
    },
    {
      q: "Apakah bisa request menu makanan prasmanan sesuai selera rombongan?",
      a: "Bisa. Tim kuliner Kenari Restaurant siap menyesuaikan menu prasmanan (halal, higienis, masakan nusantara/oriental/western, menu khusus anak sekolah, atau diet tertentu) sesuai preferensi dan anggaran panitia."
    },
    {
      q: "Bagaimana proses pemesanan dan pembayaran untuk rombongan?",
      a: "Anda cukup mengisi formulir permintaan penawaran di halaman ini atau menghubungi Corporate Sales via WhatsApp. Tim sales kami akan mengirimkan proposal & surat penawaran resmi dalam 1x24 jam. Konfirmasi dilakukan dengan Down Payment (DP) dan pelunasan sesuai kesepakatan kontrak."
    }
  ];

  return (
    <div className="bg-emerald-50/20 text-gray-800">
      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Hotel",
          "name": "Spencer Green Hotel Batu - Corporate & Group Gathering",
          "description": "Paket gathering perusahaan, instansi pemerintah, rombongan study tour sekolah, dan komunitas di Kota Batu Malang. Ballroom, meeting room, outbound & parkir bus luas.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Jl. Raya Punten No.86, Bumiaji",
            "addressLocality": "Kota Batu",
            "addressRegion": "Jawa Timur",
            "postalCode": "65338",
            "addressCountry": "ID"
          },
          "telephone": "+6281130700206",
          "url": "https://spencergreenhotel.com/corporate-gathering-batu"
        })}
      </script>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroContent.image || "https://res.cloudinary.com/dgfjos8xa/image/upload/v1767363047/event-meeting-hall-ballroom-spencer-green-hotel-batu-malang-main_vi4ivf.avif"}
            alt={heroContent.imageAlt || "Corporate Gathering & Event Ballroom Spencer Green Hotel Batu"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-900/85 to-black/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center sm:text-left">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4">
                <Briefcase className="w-4 h-4" />
                {heroContent.badge || "Solusi Lengkap Corporate Event & Rombongan di Batu Malang"}
              </div>

              <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {heroContent.title || "Venue Terbaik Gathering Perusahaan, Instansi & Rombongan"}
              </h1>

              <p className="text-emerald-100 text-base sm:text-lg lg:text-xl leading-relaxed mb-8">
                {heroContent.subtitle || "Ciptakan acara yang sukses dan berkesan di Spencer Green Hotel Batu. Fasilitas ruang pertemuan & ballroom modern, area outbound hijau, prasmanan lezat Kenari Restaurant, kamar luas, serta parkir bus pariwisata yang luas."}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <a
                  href="#rfp-form"
                  className="w-full sm:w-auto inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-base px-8 py-4 rounded-full shadow-xl transition-all"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Minta Penawaran Resmi (RFP)
                </a>

                <Button
                  onClick={() => handleDirectWhatsApp('Hero Corporate Sales')}
                  variant="outline"
                  className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30 font-medium text-base px-6 py-6 rounded-full backdrop-blur-sm"
                >
                  <Phone className="w-5 h-5 mr-2 text-emerald-400" />
                  Hubungi Corporate Sales (WA)
                </Button>
              </div>

              {/* Quick Metrics Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-emerald-700/60 text-white text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Kapasitas s/d 200 Pax</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Area Parkir Bus Luas</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Syarat Administrasi SPJ Resmi</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Proposal Fast-Response</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Target Audiences: 5 Segmen Utama */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Kebutuhan Khusus Anda
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Solusi Terpercaya untuk 5 Kategori Rombongan
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Kami menyesuaikan fasilitas, format acara, susunan ruangan, dan paket konsumsi sesuai karakteristik unik setiap kelompok.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {targetAudiences.map((aud, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-emerald-50/40 rounded-3xl p-8 border border-emerald-100 hover:border-emerald-300 hover:shadow-lg transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-6 border border-emerald-100">
                    {aud.icon}
                  </div>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full uppercase tracking-wider">
                    {aud.tag}
                  </span>
                  <h3 className="font-display text-2xl font-bold text-gray-900 mt-3 mb-3">{aud.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">{aud.desc}</p>

                  <div className="space-y-2 mb-6">
                    {aud.bullets.map((b, bIdx) => (
                      <div key={bIdx} className="flex items-center text-xs font-medium text-gray-700 gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, group_type: aud.title }));
                    const el = document.getElementById('rfp-form');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white rounded-xl text-sm font-semibold transition-colors"
                >
                  Minta Penawaran {aud.title.split(' ')[0]}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fasilitas Khusus Corporate & Group */}
      <section className="py-16 sm:py-20 bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-400 font-semibold tracking-wider text-xs uppercase bg-emerald-900 px-3 py-1 rounded-full">
              Fasilitas Lengkap Terpadu
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Semua yang Dibutuhkan untuk Kelancaran Acara Anda
            </h2>
            <p className="text-emerald-200 text-base sm:text-lg">
              Dari ruang rapat profesional hingga area relaksasi malam hari, seluruh fasilitas tersedia dalam satu lokasi yang asri.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilitiesList.map((fac, idx) => (
              <div key={idx} className="bg-emerald-900/60 p-6 rounded-2xl border border-emerald-800">
                <div className="w-12 h-12 rounded-xl bg-emerald-800 flex items-center justify-center mb-4 text-emerald-300">
                  {fac.icon}
                </div>
                <h3 className="font-bold text-lg text-white mb-2">{fac.title}</h3>
                <p className="text-emerald-200 text-xs sm:text-sm leading-relaxed">{fac.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Paket Gathering & Corporate */}
      <section className="py-16 sm:py-20 bg-emerald-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Pilihan Paket Fleksibel
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Paket Pertemuan & Gathering Sesuai Anggaran
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Tersedia paket harian tanpa menginap hingga paket fullboard residential lengkap.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`bg-white rounded-3xl p-6 border flex flex-col justify-between relative shadow-soft ${pkg.badge ? 'border-emerald-500 ring-2 ring-emerald-500/20' : 'border-gray-200'}`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">
                    {pkg.badge}
                  </div>
                )}
                <div>
                  <h3 className="font-display text-xl font-bold text-gray-900 mb-1">{pkg.name}</h3>
                  <p className="text-xs text-emerald-700 font-medium mb-3">{pkg.duration}</p>
                  <p className="text-lg font-bold text-gray-900 mb-6">{pkg.price}</p>

                  <div className="space-y-2.5 mb-6">
                    {pkg.includes.map((inc, iIdx) => (
                      <div key={iIdx} className="flex items-start text-xs text-gray-600 gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{inc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <a
                  href="#rfp-form"
                  onClick={() => setFormData(prev => ({ ...prev, notes: `Minat paket: ${pkg.name}` }))}
                  className="w-full text-center bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs sm:text-sm py-3 rounded-xl transition-colors"
                >
                  Pilih Paket Ini
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulir Permintaan Penawaran (RFP - Request for Proposal) */}
      <section id="rfp-form" className="py-16 sm:py-24 bg-white relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-50/60 rounded-3xl p-6 sm:p-10 border border-emerald-100 shadow-xl">
            <div className="text-center mb-8">
              <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
                Formulir Resmi
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-2">
                Permintaan Penawaran Harga (RFP)
              </h2>
              <p className="text-gray-600 text-sm sm:text-base max-w-xl mx-auto">
                Isi rincian kegiatan rombongan Anda di bawah ini. Tim Corporate Sales Spencer Green Hotel akan merancang proposal penawaran resmi terbaik dalam 1x24 jam.
              </p>
            </div>

            {isSubmitted ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-emerald-200">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">Terima Kasih! Permintaan Terkirim</h3>
                <p className="text-gray-600 text-sm max-w-md mx-auto mb-6">
                  Data penawaran Anda telah masuk ke sistem kami. Tim sales kami akan segera menghubungi Anda melalui WhatsApp / Email.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  <Button
                    onClick={() => {
                      const msg = generateWhatsAppMessage(formData);
                      window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 py-3 font-semibold text-sm"
                  >
                    Buka Chat WhatsApp Sales Sekarang
                  </Button>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="rounded-full px-6 py-3 text-sm"
                  >
                    Kirim Permintaan Lain
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">
                      Nama Instansi / Perusahaan / Sekolah / Komunitas <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      required
                      placeholder="Contoh: PT Sumber Makmur / Dinas Pariwisata"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">
                      Nama PIC / Kontak Person <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      required
                      placeholder="Nama Lengkap Penanggung Jawab"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Jabatan / Posisi</Label>
                    <Input
                      type="text"
                      placeholder="Contoh: Ketua Panitia / HRD"
                      value={formData.role_title}
                      onChange={(e) => setFormData({ ...formData, role_title: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">
                      Nomor WhatsApp / HP <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="tel"
                      required
                      placeholder="0812xxxxxxxx"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Alamat Email</Label>
                    <Input
                      type="email"
                      placeholder="email@instansi.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Kategori Rombongan</Label>
                    <select
                      value={formData.group_type}
                      onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                      className="mt-1 w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="Gathering Perusahaan">Gathering Perusahaan / BUMN</option>
                      <option value="Instansi Pemerintah">Instansi Pemerintah (Bimtek / Raker)</option>
                      <option value="Sekolah / Universitas">Sekolah / Kampus (Study Tour / LDKS)</option>
                      <option value="Rombongan Wisata">Rombongan Wisata / Tour & Travel</option>
                      <option value="Komunitas / Organisasi">Komunitas / Klub / Reuni</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Estimasi Peserta (Pax)</Label>
                    <Input
                      type="number"
                      min="5"
                      placeholder="Contoh: 50"
                      value={formData.participants_count}
                      onChange={(e) => setFormData({ ...formData, participants_count: e.target.value })}
                      className="mt-1 bg-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] font-semibold text-gray-700 uppercase">Check-in</Label>
                      <Input
                        type="date"
                        value={formData.check_in}
                        onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                        className="mt-1 bg-white text-xs px-2"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold text-gray-700 uppercase">Check-out</Label>
                      <Input
                        type="date"
                        value={formData.check_out}
                        onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                        className="mt-1 bg-white text-xs px-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Requirements Checkbox Buttons */}
                <div>
                  <Label className="text-xs font-semibold text-gray-700 uppercase mb-2 block">
                    Kebutuhan Fasilitas (Pilih yang sesuai):
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {availableRequirements.map((req, idx) => {
                      const isChecked = formData.requirements.includes(req);
                      return (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => handleRequirementToggle(req)}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer text-xs sm:text-sm font-medium transition-all select-none text-left w-full ${isChecked ? 'bg-emerald-100/70 border-emerald-500 text-emerald-900 shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${isChecked ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 bg-white'}`}>
                            {isChecked && (
                              <svg className="w-3 h-3 stroke-current" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                          <span>{req}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-700 uppercase">
                    Catatan Tambahan / Kebutuhan Khusus / Estimasi Budget
                  </Label>
                  <textarea
                    rows={3}
                    placeholder="Contoh: Butuh panggung 4x3 meter, sound system 5000 watt, dan makan malam prasmanan menu masakan Jawa."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 w-full bg-white border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                  />
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6 rounded-xl text-base shadow-lg transition-transform hover:scale-[1.01]"
                  >
                    {isSubmitting ? 'Mengirim Data...' : 'Kirim Permintaan Penawaran & Hubungkan ke WA'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>

                  <Button
                    type="button"
                    onClick={() => {
                      const msg = generateWhatsAppMessage(formData);
                      window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    variant="outline"
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold py-6 px-6 rounded-xl text-base"
                  >
                    <Phone className="w-5 h-5 mr-2 text-emerald-600" />
                    Kirim via WA Saja
                  </Button>
                </div>
                <p className="text-[11px] text-gray-400 text-center">
                  Data Anda aman dan hanya digunakan untuk keperluan koordinasi penawaran harga oleh tim Sales resmi Spencer Green Hotel.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Direct Contact Card & Team Support */}
      <section className="py-16 bg-emerald-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-800/80 rounded-3xl p-8 sm:p-10 border border-emerald-700 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="space-y-3 text-center lg:text-left">
              <span className="text-emerald-300 font-semibold tracking-wider text-xs uppercase bg-emerald-950 px-3 py-1 rounded-full">
                Fast-Response Corporate Sales
              </span>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                {salesBannerContent.title || "Butuh Respons Cepat Hari Ini?"}
              </h3>
              <p className="text-emerald-200 text-sm sm:text-base max-w-xl">
                {salesBannerContent.subtitle || "Hubungi tim sales & marketing kami langsung untuk survei lokasi (site visit), pengecekan ketersediaan tanggal, atau konsultasi susunan acara."}
              </p>
              <div className="pt-2 flex flex-wrap gap-4 text-xs sm:text-sm text-emerald-100 justify-center lg:justify-start">
                <div className="flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span>WhatsApp: {salesBannerContent.whatsapp || "+62 811-3070-0206"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-emerald-400" />
                  <span>Email: {salesBannerContent.email || "reservasi@spencergreenhotel.com"}</span>
                </div>
              </div>
            </div>

            <Button
              onClick={() => handleDirectWhatsApp('Fast Response Banner')}
              className="bg-white text-emerald-900 hover:bg-emerald-50 font-bold text-base px-8 py-6 rounded-full flex-shrink-0 shadow-lg"
            >
              <Phone className="w-5 h-5 mr-2 text-emerald-600" />
              Chat WhatsApp Sales Sekarang
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Pertanyaan Panitia
            </span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-3 mb-2">
              FAQ Corporate & Group Gathering di Spencer Green Hotel
            </h2>
            <p className="text-gray-500 text-sm">
              Pertanyaan yang sering diajukan seputar kapasitas, fasilitas, dan prosedur pemesanan.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl overflow-hidden transition-colors"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full text-left p-5 flex items-center justify-between font-semibold text-gray-900 hover:text-emerald-700 bg-white transition-colors"
                  >
                    <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="p-5 pt-0 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-emerald-50/20">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateGroup;
